import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Button, 
  AppBar, 
  Toolbar,
  TextField,
  Grid,
  Paper,
  MenuItem,
  Alert,
  CircularProgress
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { emailAPIService } from '../services/emailAPIService';

const Contact = () => {
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventType: '',
    eventDate: '',
    location: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState('error');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const eventTypes = [
    'Wedding',
    'Portrait Session',
    'Family Photos',
    'Engagement',
    'Maternity',
    'Newborn',
    'Headshots',
    'Corporate Event',
    'Other'
  ];

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    // Allows various formats: (555) 123-4567, 555-123-4567, 555.123.4567, 5551234567
    const phoneRegex = /^[\+]?[1-9]?[\-\s\.]?\(?[0-9]{3}\)?[\-\s\.]?[0-9]{3}[\-\s\.]?[0-9]{4}$/;
    return phone === '' || phoneRegex.test(phone.replace(/\s/g, ''));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required field validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.eventType) {
      newErrors.eventType = 'Event type is required';
    }

    // Phone validation (optional but must be valid if provided)
    if (formData.phone && !validatePhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowAlert(false);
    setIsSubmitting(true);

    if (validateForm()) {
      try {
        console.log('📤 Submitting inquiry:', formData);
        const result = await emailAPIService.submitInquiry(formData);
        
        if (result.success) {
          setAlertMessage(result.message);
          setAlertSeverity('success');
          setShowAlert(true);
          
          // Clear form on success
          setFormData({
            name: '',
            email: '',
            phone: '',
            eventType: '',
            eventDate: '',
            location: '',
            message: ''
          });
          
          // Scroll to top to show success message
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          setAlertMessage(result.error);
          setAlertSeverity('error');
          setShowAlert(true);
        }
      } catch (error) {
        console.error('❌ Unexpected error:', error);
        setAlertMessage('An unexpected error occurred. Please try again.');
        setAlertSeverity('error');
        setShowAlert(true);
      }
    } else {
      setAlertMessage('Please fix the errors below before submitting.');
      setAlertSeverity('error');
      setShowAlert(true);
    }
    
    setIsSubmitting(false);
  };

  return (
    <>
      {/* Navigation Bar - Matching Home Page Style */}
      <AppBar 
        position="static" 
        sx={{
          backgroundColor: '#062e22', // Dark forest green
          backdropFilter: 'blur(10px)',
          boxShadow: '0 2px 20px rgba(0,0,0,0.3)',
          borderBottom: '1px solid rgba(255,255,255,0.1)'
        }}
      >
        <Container maxWidth="lg">
          <Toolbar sx={{ justifyContent: 'space-between', py: 1 }}>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 300,
                color: 'white',
                fontFamily: '"Cormorant Garamond", "Playfair Display", serif',
                fontSize: '1.8rem',
                letterSpacing: '0.5px',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  color: '#c7d2cc',
                  transform: 'translateY(-1px)',
                  textShadow: '0 2px 8px rgba(255,255,255,0.2)'
                }
              }}
              onClick={() => navigate('/')}
            >
              Heather Walker Photography
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                sx={{ 
                  color: 'white',
                  fontWeight: 500,
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: '#90caf9'
                  }
                }}
                onClick={() => navigate('/portfolio')}
              >
                Portfolio
              </Button>
              <Button 
                sx={{ 
                  color: 'white',
                  fontWeight: 500,
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: '#90caf9'
                  }
                }}
                onClick={() => navigate('/contact')}
              >
                Contact
              </Button>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography 
          variant="h2" 
          gutterBottom 
          align="center"
          sx={{
            fontFamily: '"Cormorant Garamond", "Playfair Display", serif',
            fontWeight: 300,
            letterSpacing: '1px',
            color: '#062e22',
            mb: 2
          }}
        >
          Contact & Booking
        </Typography>
        <Typography 
          variant="h6" 
          align="center" 
          sx={{ 
            mb: 4,
            fontFamily: '"Cormorant Garamond", serif',
            fontWeight: 300,
            color: '#555',
            maxWidth: '600px',
            mx: 'auto',
            lineHeight: 1.6
          }}
        >
          Ready to capture your special moments? Fill out the form below and we'll get back to you within 24 hours.
        </Typography>

        {showAlert && (
          <Alert 
            severity={alertSeverity} 
            sx={{ 
              mb: 3,
              borderRadius: '12px',
              fontFamily: '"Cormorant Garamond", serif',
              fontSize: '1rem',
              '& .MuiAlert-message': {
                fontFamily: '"Cormorant Garamond", serif',
              }
            }}
          >
            {alertMessage}
          </Alert>
        )}

        <Paper 
          elevation={3} 
          sx={{ 
            p: 4,
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
            border: '1px solid rgba(6, 46, 34, 0.08)'
          }}
        >
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Row 1: Name and Email */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Full Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  error={!!errors.name}
                  helperText={errors.name}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  error={!!errors.email}
                  helperText={errors.email}
                />
              </Grid>
              
              {/* Row 2: Phone and Event Type */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone Number (Optional)"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  error={!!errors.phone}
                  helperText={errors.phone || "Format: (555) 123-4567"}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Event Type"
                  name="eventType"
                  value={formData.eventType}
                  onChange={handleChange}
                  required
                  error={!!errors.eventType}
                  helperText={errors.eventType}
                  sx={{
                    '& .MuiInputBase-input': {
                      minWidth: '140px',
                    }
                  }}
                >
                  {eventTypes.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              
              {/* Row 3: Event Date and Location */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Event Date"
                  name="eventDate"
                  type="date"
                  value={formData.eventDate}
                  onChange={handleChange}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Location"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Tell us about your event"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Share any details about your vision, timeline, or special requirements..."
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                  <Button 
                    variant="outlined" 
                    onClick={() => navigate('/')}
                    disabled={isSubmitting}
                    sx={{
                      borderColor: '#062e22',
                      color: '#062e22',
                      fontFamily: '"Cormorant Garamond", serif',
                      fontWeight: 500,
                      '&:hover': {
                        borderColor: '#041f18',
                        color: '#041f18',
                        backgroundColor: 'rgba(6, 46, 34, 0.04)'
                      }
                    }}
                  >
                    Back to Home
                  </Button>
                  <Button 
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={isSubmitting}
                    startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
                    sx={{
                      backgroundColor: '#1976d2', // Your CTA blue
                      color: 'white',
                      fontFamily: '"Cormorant Garamond", serif',
                      fontWeight: 600,
                      px: 4,
                      py: 1.5,
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontSize: '1.1rem',
                      boxShadow: '0 4px 20px rgba(25, 118, 210, 0.3)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: '#1565c0',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 25px rgba(25, 118, 210, 0.4)',
                      },
                      '&:active': {
                        backgroundColor: '#0d47a1',
                        transform: 'translateY(0px)',
                      },
                      '&:disabled': {
                        backgroundColor: '#cccccc',
                        transform: 'none',
                        boxShadow: 'none'
                      }
                    }}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Inquiry'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Container>
    </>
  );
};

export default Contact;